import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import PageHeader from '@components/layout/page-header';
import { Breadcrumbs } from '@components/atoms/ui-kit/breadcrumbs';
import { PATHS } from '../../router/paths';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { type GridAction } from '@components/atoms/datatable/grid-action-controller';
import SummaryCards from '@components/back-office/usage-revenue/summary-cards';
import MonthSelector from '@components/back-office/usage-revenue/month-selector';
import {
  PencilLine,
  ChevronDown,
  ChevronRight,
  AlertTriangle
} from 'lucide-react';
import { fmtNumber } from '@utils/format';
import { formatCurrency as fmtCurrency } from '@utils/helpers/currency';
import { SupportedCurrency } from '@utils/types/organization/organization';

// Types
import { SubOrgUsageRow } from '@utils/types/back-office/usage-revenue';

// Hooks
import { useGetResellerDetail } from '@utils/api/back-office/usage-revenue';

type Params = {
  resellerId: string;
};

export default function ResellerUsageDetail() {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const navigate = useNavigate();
  const { resellerId } = useParams<Params>();

  const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };

  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);

  // Fetch reseller detail data
  const {
    data: resellerDetailResponse,
    isLoading,
    isError
  } = useGetResellerDetail(resellerId, selectedMonth);

  const resellerDetail = resellerDetailResponse?.result;

  // The reseller's own currency for its totals; each sub-org row is formatted
  // with the one on that row, because a child can bill in a different currency
  // from its parent. This used to hardcode EUR, which showed SoftwareONE
  // Philippines — a USD account — in euros.
  const resellerCurrency: SupportedCurrency =
    resellerDetail?.reseller?.currency ?? 'EUR';

  const formatCurrency = (value: number, rowCurrency?: SupportedCurrency) =>
    fmtCurrency(value, rowCurrency ?? resellerCurrency);

  type Row = SubOrgUsageRow;

  const columns = useMemo<DataGridColumn<Row>[]>(
    () => [
      {
        id: 'expand',
        header: '',
        field: 'organization_id',
        accessorKey: 'organization_id',
        maxWidth: 40,
        minWidth: 40,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ row }) => (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              setExpandedRowId(
                expandedRowId === row.original.organization_id
                  ? null
                  : row.original.organization_id
              );
            }}
            className="p-1 rounded-2xl hover:bg-black/5 dark:hover:bg-white/10"
          >
            {expandedRowId === row.original.organization_id ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        )
      },
      {
        id: 'organization_id',
        header: t('id'),
        field: 'organization_id',
        accessorKey: 'organization_id',
        maxWidth: 80,
        minWidth: 60,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'number',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'organization_name',
        header: t('organization'),
        field: 'organization_name',
        accessorKey: 'organization_name',
        minWidth: 200,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => (
          <span className="font-medium">{String(getValue() ?? '')}</span>
        )
      },
      {
        id: 'stt_units',
        header: t('stt-units'),
        field: 'stt_units',
        accessorKey: 'stt_units',
        minWidth: 100,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => fmtNumber(getValue() ?? 0)
      },
      {
        id: 'alignment_units',
        header: t('alignment-units'),
        field: 'alignment_units',
        accessorKey: 'alignment_units',
        minWidth: 120,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => fmtNumber(getValue() ?? 0)
      },
      {
        id: 'translation_characters',
        header: t('translation-chars'),
        field: 'translation_characters',
        accessorKey: 'translation_characters',
        minWidth: 130,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => fmtNumber(getValue() ?? 0)
      },
      {
        id: 'total_cost',
        header: t('cost'),
        field: 'total_cost',
        accessorKey: 'total_cost',
        minWidth: 100,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue, row }) =>
          formatCurrency(Number(getValue() ?? 0), row.original.currency)
      },
      {
        id: 'billed',
        header: t('billed'),
        field: 'billed',
        accessorKey: 'billed',
        minWidth: 100,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue, row }) =>
          formatCurrency(Number(getValue() ?? 0), row.original.currency)
      }
    ],
    [t, expandedRowId]
  );

  const openCustomer = useCallback(
    (row: Row) => {
      navigate(PATHS(String(row.organization_id)).CUSTOMER_CONTENT);
    },
    [navigate]
  );

  const actions = useMemo<GridAction<Row>[]>(
    () => [
      {
        id: 'edit',
        label: t('view-customer'),
        icon: <PencilLine className="h-3.5 w-3.5" />,
        intent: 'primary',
        run: openCustomer
      }
    ],
    [t, navigate, openCustomer]
  );

  const breadcrumbItems: Array<{ label: string; path?: string }> = [
    { label: t('usage-and-revenue'), path: PATHS().BO_USAGE_REVENUE }
  ];

  return (
    <div className="bg-app flex-1 px-4 py-2">
      {isError && (
        <div className="flex items-center gap-2 rounded-2xl bg-red-500/10 border border-red-500/25 px-4 py-3 mb-4 text-sm text-red-600 dark:text-red-400">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>{t('failed-to-load-data')}</span>
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <PageHeader
          title={
            <Breadcrumbs
              items={breadcrumbItems}
              currentFolderName={
                resellerDetail?.reseller?.name || t('reseller')
              }
              rootLabel={t('usage-and-revenue')}
              rootPath={PATHS().BO_USAGE_REVENUE}
            />
          }
          caption={t('view-reseller-usage-breakdown')}
        />
        <MonthSelector value={selectedMonth} onChange={setSelectedMonth} />
      </div>

      <SummaryCards
        totalUsage={
          (resellerDetail?.totals?.stt_units || 0) +
          (resellerDetail?.totals?.alignment_units || 0)
        }
        totalBilled={resellerDetail?.totals?.total_billed || 0}
        forecast={resellerDetail?.totals?.forecast || 0}
        currency={resellerCurrency}
        isLoading={isLoading}
        className="mb-6"
      />

      <div>
        <h3
          className="text-sm font-semibold mb-4"
          style={{ color: getColour('normalText') }}
        >
          {t('sub-organizations')} (
          {resellerDetail?.sub_organizations?.length || 0})
        </h3>

        <DataGridV2<Row>
          data={resellerDetail?.sub_organizations || []}
          columns={columns}
          actions={actions}
          enableRowSelection={false}
          enablePagination
          enableSorting
          enableFiltering
          totalItems={resellerDetail?.sub_organizations?.length || 0}
          pageSize={25}
          pageSizeOptions={[25, 50, 75, 100]}
          showColumnFilters={false}
          filterMode="popover"
          height="50vh"
          dense
          grid={false}
          striped
          loading={isLoading}
          getRowId={row => String(row.organization_id)}
          onRowClick={openCustomer}
        />
      </div>
    </div>
  );
}
